import { useCallback, useEffect, useReducer, useRef } from 'react';
import { supabase } from '../services/supabase';

export interface TableRequest {
  id: string;
  store_id: string;
  table_id: string | null;
  table_number: number | null;
  type: string;
  status: string;
  created_at: string;
  ack_at: string | null;
}

type Action =
  | { type: 'set'; requests: TableRequest[] }
  | { type: 'upsert'; request: TableRequest }
  | { type: 'remove'; id: string };

function reducer(state: TableRequest[], action: Action): TableRequest[] {
  switch (action.type) {
    case 'set':
      return [...action.requests].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    case 'upsert': {
      // If status moved out of pending, drop it from the active list
      if (action.request.status && action.request.status !== 'pending') {
        return state.filter((r) => r.id !== action.request.id);
      }
      const next = [
        action.request,
        ...state.filter((r) => r.id !== action.request.id),
      ];
      return next.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    }
    case 'remove':
      return state.filter((r) => r.id !== action.id);
    default:
      return state;
  }
}

async function callEdge(name: string, body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw error;
  return data;
}

export interface UseTableRequestsOptions {
  onInsert?: (req: TableRequest) => void;
}

export function useTableRequests(storeId: string, opts: UseTableRequestsOptions = {}) {
  const [requests, dispatch] = useReducer(reducer, [] as TableRequest[]);
  const tableMapRef = useRef<Map<string, number>>(new Map());
  const onInsertRef = useRef(opts.onInsert);
  onInsertRef.current = opts.onInsert;

  const enrich = useCallback((row: Record<string, unknown>): TableRequest => {
    const tableId = (row.table_id as string | null) ?? null;
    return {
      id: row.id as string,
      store_id: row.store_id as string,
      table_id: tableId,
      table_number: tableId ? tableMapRef.current.get(tableId) ?? null : null,
      type: (row.type as string) ?? 'call_waiter',
      status: (row.status as string) ?? 'pending',
      created_at: (row.created_at as string) ?? new Date().toISOString(),
      ack_at: (row.ack_at as string | null) ?? null,
    };
  }, []);

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;

    const init = async () => {
      const { data: tables } = await supabase
        .from('restaurant_tables')
        .select('id, table_number')
        .eq('store_id', storeId);
      const map = new Map<string, number>();
      (tables ?? []).forEach((t) => {
        const tt = t as Record<string, unknown>;
        map.set(tt.id as string, tt.table_number as number);
      });
      tableMapRef.current = map;

      const { data } = await supabase
        .from('table_requests')
        .select('*')
        .eq('store_id', storeId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(100);

      if (cancelled) return;
      dispatch({
        type: 'set',
        requests: (data ?? []).map((r) => enrich(r as Record<string, unknown>)),
      });
    };

    void init();

    const channel = supabase
      .channel(`staff-table-requests-${storeId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'table_requests',
          filter: `store_id=eq.${storeId}`,
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            const id = (payload.old as Record<string, unknown> | null)?.id as string | undefined;
            if (id) dispatch({ type: 'remove', id });
            return;
          }
          const req = enrich(payload.new as Record<string, unknown>);
          dispatch({ type: 'upsert', request: req });
          if (payload.eventType === 'INSERT' && req.status === 'pending') {
            onInsertRef.current?.(req);
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [storeId, enrich]);

  const ack = useCallback(async (id: string) => {
    try {
      await callEdge('tapnpay-call-waiter-ack', { request_id: id, action: 'ack' });
    } catch {
      // Fallback to direct UPDATE if edge function not available
      await supabase
        .from('table_requests')
        .update({ status: 'ack', ack_at: new Date().toISOString() })
        .eq('id', id);
    }
  }, []);

  const resolve = useCallback(async (id: string) => {
    try {
      await callEdge('tapnpay-call-waiter-ack', { request_id: id, action: 'resolve' });
    } catch {
      await supabase
        .from('table_requests')
        .update({ status: 'resolved' })
        .eq('id', id);
    }
    dispatch({ type: 'remove', id });
  }, []);

  return { requests, ack, resolve };
}
